import i18n, { toAppLanguage } from "@/i18n";
import { getLocale } from "@/i18n/formatters";

const getActiveLocale = () =>
  getLocale(toAppLanguage(i18n.resolvedLanguage || i18n.language));

export const createAppointmentDate = (dateStr: string, timeStr?: string): Date => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return new Date();

  const [year, month, day] = dateStr.split("-").map(Number);
  if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1 || day > 31) {
    return new Date();
  }

  if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) {
    return new Date(year, month - 1, day, 12);
  }

  const [hours, minutes] = timeStr.split(":").map(Number);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return new Date(year, month - 1, day, 12);
  }

  return new Date(year, month - 1, day, hours, minutes);
};

export const createAppointmentDateTime = (dateStr: string, timeStr: string): Date =>
  createAppointmentDate(dateStr, timeStr);

export const formatAppointmentDate = (
  dateStr: string,
  options: Intl.DateTimeFormatOptions | {
    compact?: boolean;
    includeYear?: boolean;
  } = {},
): string => {
  const normalizedDate = /^\d{4}-\d{2}-\d{2}T/.test(dateStr)
    ? dateStr.slice(0, 10)
    : dateStr;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) return dateStr;

  const legacyOptions = options as Intl.DateTimeFormatOptions;
  if ("month" in legacyOptions || "year" in legacyOptions || "weekday" in legacyOptions) {
    return createAppointmentDate(normalizedDate).toLocaleDateString(
      getActiveLocale(),
      legacyOptions,
    );
  }

  const { compact = false, includeYear = true } = options as {
    compact?: boolean;
    includeYear?: boolean;
  };
  return createAppointmentDate(normalizedDate).toLocaleDateString(
    getActiveLocale(),
    compact
      ? { day: "numeric", month: "short", ...(includeYear && { year: "numeric" }) }
      : {
          weekday: "long",
          day: "numeric",
          month: "long",
          ...(includeYear && { year: "numeric" }),
        },
  );
};

export const compareAppointmentDates = (dateA: string, dateB: string): number =>
  dateA.localeCompare(dateB);

export const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getCurrentAppointmentDateTime = (): { date: string; time: string } => {
  const now = new Date();
  return {
    date: getTodayDateString(),
    time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
  };
};

export const isValidAppointmentTime = (timeStr: string): boolean => {
  if (!/^([0-1]?\d|2[0-3]):[0-5]\d$/.test(timeStr)) return false;

  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours >= 9 && hours < 18 && (minutes === 0 || minutes === 30);
};
